import React, { useRef } from 'react';
import Autosuggest from 'react-autosuggest';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import RelativePortal from 'react-relative-portal';
import { useDebouncedCallback } from 'use-lodash-debounce';
import theme from './search/autosuggest.module.css';

theme.input = 'form-control';

const Search = React.forwardRef((props, ref) => {
  const { controlId, placeholder, suggestions, value, readOnly, disabled, className, allowDisable, ...rest } = props;
  const autosuggest = useRef(null);

  const onBlur = (event, { highlightedSuggestion }) => {
    props.onBlur(highlightedSuggestion);
  };

  const onFocus = event => {
    event.target.select();

    if (props.onFocus) {
      props.onFocus(event);
    }
  };

  const onKeyDown = event => {
    // Enter was pressed
    if (event.which === 13 || event.keyCode === 13) {
      if (event.target.value !== '') {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  };

  const debouncedLoadSuggestions = useDebouncedCallback(props.loadSuggestions, props.debounce);

  const onSuggestionsFetchRequested = ({ value }) => {
    debouncedLoadSuggestions(value);
  };

  const renderSuggestionsContainer = ({ containerProps, children }) => {
    const rightOffset = getBounds(containerProps);

    return (
      <RelativePortal component="div" left={rightOffset || 0} top={0} right={-1} fullWidth>
        <div {...containerProps} className={classNames('suggestion-wrapper', { 'allow-disable': allowDisable })}>
          {children}
        </div>
      </RelativePortal>
    );
  };

  const inputProps = {
    className: classNames('form-control', className),
    disabled,
    id: controlId,
    onBlur,
    onChange: (event, { newValue }) => props.onChange(newValue),
    onFocus,
    onKeyDown,
    placeholder,
    readOnly,
    value,
    ref,
  };

  return (
    <Autosuggest
      id={controlId}
      key={controlId}
      ref={autosuggest}
      suggestions={suggestions}
      theme={theme}
      inputProps={inputProps}
      focusInputOnSuggestionClick={false}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      renderSuggestionsContainer={renderSuggestionsContainer}
      {...rest}
    />
  );
});

const getBounds = container => {
  // min width for suggestion is 300px;
  const MAX_SUGG_SIZE = 300;

  if (!container.id) {
    return;
  }

  const el = document.getElementById(container.id.replace('react-autowhatever-', ''));

  if (!el) {
    return;
  }

  // Element will already get max suggestion size
  const rect = el.getBoundingClientRect();
  if (rect.width > MAX_SUGG_SIZE) {
    return;
  }

  // Element not close enough to edge for it to matter
  const screenSize = window.innerWidth || document.documentElement.clientWidth;
  if (rect.left + MAX_SUGG_SIZE < screenSize) {
    return;
  }

  // Suggestion won't fit -- offset for relativePortal
  return (MAX_SUGG_SIZE - rect.width) * -1;
};

Search.defaultProps = {
  debounce: 100,
  highlightFirstSuggestion: true,
  onChange: v => v,
  onSubmit: () => {},
  shouldRenderSuggestions: value => String(value).trim().length > 0,
  placeholder: 'Search',
  value: '',
};

Search.propTypes = {
  controlId: PropTypes.string,
  debounce: PropTypes.number,
  disabled: PropTypes.bool,
  getSuggestionValue: PropTypes.func,
  highlightFirstSuggestion: PropTypes.bool,
  loadSuggestions: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onSubmit: PropTypes.func,
  onSuggestionSelected: PropTypes.func,
  onSuggestionsClearRequested: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  renderSuggestion: PropTypes.func,
  renderSuggestionsContainer: PropTypes.func,
  shouldRenderSuggestions: PropTypes.func,
  suggestions: PropTypes.array,
  value: PropTypes.string,
};

export default Search;
